import React, { useState } from 'react';
import { User, Upload, X, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ImageProfileUploaderProps {
  imageFile: File | null;
  onImageFileChange: (file: File | null) => void;
  existingImageUrl?: string | null;
  isUploading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  alt?: string;
}

/**
 * A reusable component for handling profile image uploads
 * Can be used for personas, user profiles, account images, etc.
 * Only selects the file for preview, actual upload happens when the form is submitted
 */
export const ImageProfileUploader: React.FC<ImageProfileUploaderProps> = ({
  imageFile,
  onImageFileChange,
  existingImageUrl,
  isUploading = false,
  size = 'md',
  alt = 'Profile image'
}) => {
  const { t } = useTranslation();
  const [dragActive, setDragActive] = useState(false);

  // Size classes based on the size prop
  const sizeClasses = {
    sm: 'w-16 h-16',
    md: 'w-24 h-24 md:w-32 md:h-32',
    lg: 'w-36 h-36 md:w-48 md:h-48'
  };

  const iconSizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10 md:w-12 md:h-12',
    lg: 'w-12 h-12 md:w-16 md:h-16'
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      validateAndSetFile(file);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file: File) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert(t('common.errors.invalidImageType', 'Please upload an image file'));
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(t('common.errors.imageTooLarge', 'Image size should be less than 5MB'));
      return;
    }

    // Pass the validated file up to the parent component
    onImageFileChange(file);
  };

  const handleRemove = () => {
    onImageFileChange(null);
  };

  // Generate a unique ID for the input to avoid conflicts when multiple uploaders are used
  const uploadInputId = `image-profile-upload-${Math.random().toString(36).substring(2, 9)}`;

  return (
    <div className="flex-shrink-0">
      <div className={`${sizeClasses[size]} flex-shrink-0`}>
        {/* Image Preview Area */}
        <div 
          className={`w-full h-full rounded-lg border-2 ${
            dragActive 
              ? 'border-primary-500 bg-primary-50' 
              : imageFile || existingImageUrl 
                ? 'border-gray-300' 
                : 'border-dashed border-gray-300'
          } flex items-center justify-center bg-gray-50 overflow-hidden relative`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {/* Display the image if available */}
          {imageFile ? (
            <img
              src={URL.createObjectURL(imageFile)}
              alt={`${alt} Preview`}
              className="w-full h-full object-cover"
            />
          ) : existingImageUrl ? (
            <img
              src={existingImageUrl}
              alt={alt}
              className="w-full h-full object-cover"
            />
          ) : (
            <User className={`${iconSizeClasses[size]} text-gray-400`} />
          )}

          {/* Hidden file input */}
          <input
            type="file"
            id={uploadInputId}
            accept="image/*"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            onChange={handleChange}
            disabled={isUploading}
          />

          {/* Controls for editing/removing when an image is selected */}
          {(imageFile || existingImageUrl) && (
            <div className="absolute top-1 right-1 flex gap-1">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemove();
                }}
                className="p-1 bg-white rounded-md shadow-sm hover:bg-gray-50 text-gray-600"
                disabled={isUploading}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Upload button/label below the image */}
      <label
        htmlFor={uploadInputId}
        className="mt-2 flex justify-center text-xs text-primary-600 hover:text-primary-500 cursor-pointer"
      >
        {isUploading ? (
          <div className="flex items-center">
            <div className="animate-spin h-3 w-3 mr-2 border-2 border-primary-500 border-t-transparent rounded-full"></div>
            {t('common.uploading', 'Uploading...')}
          </div>
        ) : (
          <>
            <Upload className="w-3 h-3 mr-1" />
            {imageFile || existingImageUrl 
              ? t('common.changeImage', 'Change Image')
              : t('common.uploadImage', 'Upload Image')
            }
          </>
        )}
      </label>
    </div>
  );
}; 